import type { RSUAsset, ShareAsset } from "~/types/assets.types";
import type { Fiat } from "~/types/global.types";
import { calculateOwnership } from "~/utils/calculations.utils";
import { convertCurrency } from "../genericCalculations/convertCurrency";
import { calculateOnlyStocksPortfolio } from "./partInPortfolio";
import { RsuTags } from "~/utils/tagEnums";


export const calculateRSUsProfit = (
  userProduct: RSUAsset,
  baseCurrency: Fiat,
  currentCurrency: Fiat
) => {
  const stocks = userProduct?.rsuData || [];
  const shareProfits = calculateRSUShareProfit(userProduct);
  const ownership = userProduct?.ownership || 0;

  const totalProfit = stocks.reduce((sum, _, index) => {
    const shareProfit = shareProfits?.[index] || 0;
    const shareProfitOwnership = calculateOwnership(shareProfit, ownership);

    return sum + shareProfitOwnership;
  }, 0);

  return convertCurrency(totalProfit, baseCurrency, currentCurrency);
};

export const calculateStocksProfit = (
  userProduct: ShareAsset,
  baseCurrency: Fiat,
  currentCurrency: Fiat
) => {
  const stocks = userProduct?.stocksData || [];
  const shareProfits = calculateStocksShareProfit(userProduct);
  const ownership = userProduct?.ownership || 0;

  const totalProfit = stocks.reduce((sum, _, index) => {
    const shareProfit = shareProfits?.[index] || 0;
    const shareProfitOwnership = calculateOwnership(shareProfit, ownership);

    return sum + shareProfitOwnership;
  }, 0);

  return convertCurrency(totalProfit, baseCurrency, currentCurrency);
};

export const calculateStocksShareProfit = (userProduct: ShareAsset) => {
  const stocks = userProduct?.stocksData || [];

  return stocks.map((stock) => {
    const { stockPrice, purchasePrice, amount } = stock;
    if (!stockPrice || !amount) return 0;
    return purchasePrice && purchasePrice > 0
      ? (stockPrice - purchasePrice) * amount
      : 0;
  });
};

export const calculateShareProfitRate = (userProduct: ShareAsset) => {
  const stocks = userProduct?.stocksData || [];
  const shareProfits = calculateStocksShareProfit(userProduct);

  return stocks.map((stock, index) => {
    const purchasePrice = stock?.purchasePrice || 0;
    const amount = stock?.amount || 0;
    const shareProfit = shareProfits?.[index] || 0;

    if (Number(purchasePrice) === 0) return 0;
    return shareProfit / purchasePrice / amount;
  });
};

export const calculateRSUShareProfit = (userProduct: RSUAsset) => {
  const rsus = userProduct?.rsuData || [];
  const stockPrice = userProduct?.stockPrice || 0;
  return rsus.map((rsu) => {
    const { grantPrice , totalAmount } = rsu;
    if (!grantPrice || !totalAmount) return 0;
    return ((stockPrice || 0) - (grantPrice || 0)) * totalAmount;
  });
};


export const calculateESPPShareProfit = (userProduct: RSUAsset) => {
  const rsus = userProduct?.rsuData || [];
  const stockPrice = userProduct?.stockPrice || 0;
  return rsus.map((rsu) => {
    const { buyPrice , totalAmount } = rsu;
    if (!buyPrice || !totalAmount) return 0;
    return ((stockPrice || 0) - (buyPrice || 0)) * totalAmount;
  });
};

export const calculateRSUShareProfitRate = (userProduct: RSUAsset) => {
  const rsus = userProduct?.rsuData || [];
  const rsuShareProfits =  userProduct.broker === RsuTags.ESPP  ? calculateESPPShareProfit(userProduct) :  calculateRSUShareProfit(userProduct);
  return rsus.map((rsu, index) => {
    const grantPrice = rsu?.grantPrice || rsu?.buyPrice || 0;
    const amount = rsu?.totalAmount || 0;
    const rsuShareProfit = rsuShareProfits?.[index] || 0;
    if (Number(grantPrice) === 0) return 0;
    return rsuShareProfit / grantPrice / amount;
  });
};


export const calculateStocksProfitRate = (
  userProduct: ShareAsset,
  cashCurrency: Fiat,
  baseCurrency: Fiat
) => {
  const stocks = userProduct?.stocksData || [];
  const shareProfitRates = calculateShareProfitRate(userProduct);
  const partsInPortfolio = calculateOnlyStocksPortfolio(
    userProduct,
    cashCurrency,
    baseCurrency
  );

  return stocks.reduce((sum, _, index) => {
    const shareProfitRate = shareProfitRates?.[index] || 0;
    const partInPortfolio = partsInPortfolio?.[index] || 0;

    return sum + shareProfitRate * partInPortfolio;
  }, 0);
};

